import { ApolloClient, ApolloLink, ApolloProvider, from, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ApolloLinkTimeout from 'apollo-link-timeout';
import { createUploadLink } from 'apollo-upload-client';
import { createClient } from 'graphql-ws';
import React from 'react';

import environmentVariables from '../appVariables';
import { cache, notificationsVar } from '../cache/cache';
import { webSocketURL as url } from '../config';
import { HTTP_STATUS_CODE } from '../constants/apolloConfig';
import { NOTIFICATION_TEXT } from '../constants/notification';
import { isTimeoutError } from './timeout/timeoutDetection';
import { clearPendingTimeouts, clearTimeoutCount, registerTimeout } from './timeout/timeoutManager';
import SubscribeConnectionHook from '../hooks/subscribeConnectionHook/subscribeConnectionHook';
import { Mixpanel } from '../mixpanel/mixpanel';
import { refreshAuthTokenService } from '../services/auth';
import { getCookie, removeCookie } from '../utils/cookiesUtil';
import { tokenDecode } from '../utils/jwtUtils';
import { clearWhiteLabelCache } from '../utils/whiteLabelCache';

const { setSubscribeConnection } = SubscribeConnectionHook();

const authLink = setContext((_, { headers }) => {
  const token = getCookie('token');

  return {
    headers: {
      ...headers,
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };
});

const httpLink = createUploadLink({
  uri:
    environmentVariables?.environment !== 'development'
      ? `/graphql`
      : `${environmentVariables?.reactAppApiGraphqlDev}/graphql`,
  headers: {
    'Apollo-Require-Preflight': 'true',
    'x-timeout': (1000 * 60 * 5).toString(),
  },
  fetchOptions: {
    timeout: 1000 * 60 * 5,
  },
});

const logout = async (): Promise<void> => {
  const token = getCookie('token');

  if (!token) {
    return;
  }
  const tokenContent = tokenDecode(token);
  const redirectPath = tokenContent.redirectLogoutUrl ?? '/';

  Mixpanel.reset();
  removeCookie('token');
  removeCookie('preferredLanguage');
  sessionStorage.clear();
  clearWhiteLabelCache();
  clearPendingTimeouts();
  window.location.href = redirectPath;
  await client.clearStore();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleForbiddenError = async ({ operation, forward }: any): Promise<any> => {
  const token = getCookie('token');

  if (!token) {
    await logout();

    return;
  }

  const currentNumericDate = Math.round(Date.now() / 1000);
  const tokenContent = tokenDecode(token);
  const tokenExp = tokenContent.expiresIn;

  if (currentNumericDate <= tokenExp && !tokenContent.remember) {
    await logout();

    return;
  }

  try {
    const accessToken = await refreshAuthTokenService(token, tokenContent.refreshToken);
    const oldHeaders = operation.getContext().headers;

    operation.setContext({
      headers: {
        ...oldHeaders,
        authorization: `Bearer ${accessToken}`,
      },
    });

    // eslint-disable-next-line consistent-return
    return forward(operation);
  } catch (refreshError) {
    notificationsVar([
      {
        id: operation?.operationName,
        title: refreshError as string,
        description: NOTIFICATION_TEXT.errorDescription,
        autoClose: true,
        leftIcon: <WarningAmberRoundedIcon color="error" />,
      },
    ]);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const notifyGenericError = (operation: any): void => {
  notificationsVar([
    {
      id: operation?.operationName,
      title: NOTIFICATION_TEXT.errorTitle,
      description: NOTIFICATION_TEXT.errorDescription,
      autoClose: true,
      leftIcon: <WarningAmberRoundedIcon color="error" />,
    },
  ]);
};

// Handle a timed-out operation. Operations marked with
// `context: { skipTimeoutScreen: true }` fail silently (no takeover screen);
// the component handles its own error/loading state.
//
// Otherwise the timeout is recorded and the takeover screen is shown. The error
// still reaches the component — whatever error state it renders sits behind the
// overlay and is discarded when Retry remounts the route.
//
// Once an operation has spent its retry budget (see MAX_TIMEOUT_RETRIES) it
// falls through to the ordinary error notification instead, so a request that
// keeps timing out — a bad filter combination, say — cannot loop the screen.
//
// Retry does NOT touch auth: genuine auth failures surface as FORBIDDEN via
// handleForbiddenError.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleTimeout = (operation: any): void => {
  if (operation.getContext().skipTimeoutScreen) {
    return;
  }

  if (!registerTimeout(operation.operationName)) {
    notifyGenericError(operation);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logoutLink = onError(({ networkError, operation, forward }: any) => {
  if (!networkError) {
    return undefined;
  }

  const errorCode = networkError?.result?.errors?.[0]?.extensions?.code?.toString()?.toUpperCase() || '';

  if (isTimeoutError(networkError, errorCode)) {
    handleTimeout(operation);

    return undefined;
  }

  switch (errorCode) {
    case 'FORBIDDEN':
      handleForbiddenError({ operation, forward });
      break;

    case HTTP_STATUS_CODE.BadGateway:
      forward(operation);
      break;

    case HTTP_STATUS_CODE.BadRequest:
      forward(operation);
      break;

    case HTTP_STATUS_CODE.Unauthorized:
      forward(operation);
      break;

    default:
      // Callers with their own error feedback opt out of the generic alert; auth handling above is unaffected.
      if (!operation.getContext().skipGenericErrorNotification) {
        notifyGenericError(operation);
      }
      break;
  }

  return undefined;
});

const wsLink = new GraphQLWsLink(
  createClient({
    url,
    on: {
      closed: () => {
        setSubscribeConnection(false);
      },
      connected: () => {
        setSubscribeConnection(true);
      },
    },
  }),
);
const timeoutLink = new ApolloLinkTimeout(1000 * 60 * 5, HTTP_STATUS_CODE.RequestTimeout);
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);

    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink,
);

// Clears an operation's timeout history on a successful response, so a later
// unrelated blip still gets its own retry rather than going straight to a
// notification.
const timeoutResetLink = new ApolloLink((operation, forward) =>
  forward(operation).map((result) => {
    clearTimeoutCount(operation.operationName);

    return result;
  }),
);

const link = authLink.concat(timeoutResetLink).concat(timeoutLink).concat(splitLink);

// initialize apollo client
export const client = new ApolloClient({
  cache,
  link: from([logoutLink.concat(link)]),
  credentials: '*',
});

const ApolloConfig: React.FC = ({ children }) => <ApolloProvider client={client}>{children}</ApolloProvider>;

export default ApolloConfig;
